import { WMS_COMMANDS } from '../constants/wms';
import listener from './listener';
import { APP_STATE } from 'src/constants/main';
import { TLocale } from 'src/types/main';
import { isJson } from 'src/utils/helpers';

type TMessage = {
  _id: string;
  data?: any;
  command?: string;
  success: boolean;
  type: 'command' | 'response';
};

type TOptions = {
  timeout: number;
};
class WebviewMessageService {
  // Promise stack
  private stack: {
    [key: string]: any;
  } = {};

  onAppStateChange = (state: APP_STATE) => {
    listener.appStateChange(state);
  };
  //   command handlers
  private handler: {
    [key: string]: any;
  } = {
    [WMS_COMMANDS.APP_STATE_CHANGE]: this.onAppStateChange,
    // [WMS_COMMANDS.GET_FIREBASE_TOKEN]: this.getFCMToken,
  };

  constructor() {
    this.listener();
  }

  private options: TOptions = {
    timeout: 10000,
  };

  private listener = () => {
    const target = window.PLATFORM === 'android' ? document : window;
    target.addEventListener('message', (event: any) => {
      const { _id, data, success, type, command } = (isJson(event.data) ? JSON.parse(event.data) : event.data) as TMessage;
      if (type === 'response') {
        if (this.stack[`${_id}_success`] && success) {
          this.stack[`${_id}_success`](data);
        } else if (this.stack[`${_id}_fail`] && !success) {
          this.stack[`${_id}_fail`](data);
        }
        delete this.stack[`${_id}_success`];
        delete this.stack[`${_id}_fail`];
      } else if (type === 'command' && command && this.handler[command]) {
        this.handler[command](data);
      }
    });
  };

  private sendMessage = async (command: WMS_COMMANDS, data: any = {}, options: TOptions = this.options): Promise<string> =>
    // eslint-disable-next-line promise/avoid-new
    await new Promise((resolve, reject) => {
      options = {
        ...options,
        ...this.options,
      };

      const _id = Date.now().toString();

      if (!window.IS_MOBILE && !window.ReactNativeWebView) {
        reject('Not mobile app');
        return;
      }

      this.stack[`${_id}_success`] = resolve;
      this.stack[`${_id}_fail`] = reject;
      window.ReactNativeWebView.postMessage(JSON.stringify({ _id, command, data, from: 'mobile' }));

      setTimeout(() => {
        delete this.stack[`${_id}_success`];
        delete this.stack[`${_id}_fail`];
        reject('Timeout error');
      }, 10000);
    });

  actions = {
    [WMS_COMMANDS.LOGOUT]: () => this.sendMessage(WMS_COMMANDS.LOGOUT),
  };

  public getFirebaseToken = async (): Promise<string | null> => {
    try {
      const result = await this.sendMessage(WMS_COMMANDS.GET_FIREBASE_TOKEN);
      return result;
    } catch (error) {
      return null;
    }
  };

  public copyToClipboard = (data: string): null | undefined => {
    try {
      this.sendMessage(WMS_COMMANDS.COPY_TO_CLIPBOARD, data);
      return null;
    } catch (error) {
      return null;
    }
  };

  public log = (data: any) => {
    try {
      this.sendMessage(WMS_COMMANDS.LOG, data, {
        timeout: 0,
      });
    } catch (error) {
      return null;
    }
  };

  public setLoading = (data: boolean) => {
    try {
      this.sendMessage(WMS_COMMANDS.LOADING, data, {
        timeout: 0,
      });
    } catch (error) {
      return null;
    }
  };

  public setLanguage = (language: TLocale) => {
    try {
      this.sendMessage(WMS_COMMANDS.SET_LANGUAGE, language, {
        timeout: 0,
      });
    } catch (error) {
      return null;
    }
  };

  public playSound = (timeout: number) => {
    try {
      this.sendMessage(WMS_COMMANDS.PLAY_SOUND, timeout, {
        timeout: 0,
      });
    } catch (error) {
      return null;
    }
  };

  public downloadFile = async (path: string, options?: any) => {
    try {
      await this.sendMessage(WMS_COMMANDS.DOWNLOAD_FILE, path, {
        ...options,
        timeout: 0,
      });
    } catch (error) {
      return null;
    }
  };
}

const wms = new WebviewMessageService();

export default wms;
